import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { Saisie } from '../shared/ui/Champ';
import { PiedDePage } from '../shared/ui/PiedDePage';
import { Titre } from '../shared/ui/Titre';
import {
  destinationApres,
  lireLErreur,
  useConnexion,
  useDemandeDeCode,
  usePoseDeMotDePasse,
  useSessionVivante,
} from '../features/connexion/connexion';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA PAGE DE CONNEXION — `CDC-6` F-71, PAGE 6 DE LA LISTE FERMÉE DES HUIT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est la seule du produit**, et les trois surfaces y mènent : `web/` y renvoie une
 * visite non authentifiée, `admin/` « s'atteint par la page de connexion **unique** du
 * site vitrine, celle des quatre rôles ».
 *
 * **SON CONTENU EST UNE LISTE FERMÉE, ET ELLE EST COURTE** : « adresse, mot de passe,
 * "mot de passe oublié", **rien de plus** ». Pas de « rester connecté » — la session vaut
 * sept jours de toute façon. Pas de création de compte — **le produit ne s'ouvre à
 * personne tout seul**, une structure provisionne. Pas de connexion par un tiers.
 *
 * **AUCUNE ADRESSE CACHÉE, AUCUN SÉLECTEUR DE RÔLE** (`CDC-5`) : la même page pour les
 * quatre, et « une seconde route réservée à l'administration aurait suffi à trahir quelles
 * adresses en sont ».
 *
 * **LE REFUS EST IDENTIQUE DANS LES TROIS CAS**, et le temps de réponse aussi : adresse
 * inconnue, mot de passe faux, compte anonymisé. Cette page **n'a donc rien à
 * distinguer** — elle affiche ce que le serveur rend, et rien de plus.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **ELLE PORTE LE PIED DE PAGE ET PAS LE MENU**, F-71.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Elle porte le pied de page et pas le menu. **Ni carrousel, ni bloc tiers.** » Elle est
 * donc **hors du cadre commun** aux sept autres : elle prend le pied et laisse le menu. Le
 * motif dépasse la mise en page — un menu de sept sorties sur la page où l'on vient
 * *entrer*, et dont l'une ramène ici depuis `web/` ou `admin/`, tourne en rond.
 *
 * **Et « ni bloc tiers » est la plus importante des deux absences** : cette page reçoit un
 * mot de passe, et un tiers chargé dans le même document le partagerait. F-72 le pose de
 * l'autre côté — le bloc « vit sur une page à lui, et **jamais sur la page qui porte
 * l'écran de connexion** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **ELLE REFUSE LE TÉLÉPHONE, ET ELLE LE DIT AVANT TOUTE SAISIE — CRITÈRE 1.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-71 : « elle **REFUSE LE TÉLÉPHONE, ET ELLE LE DIT AVANT** — pas après qu'on a saisi son
 * mot de passe. **Seule des huit pages qui refuse un appareil.** »
 *
 * **Le refus se fait par LARGEUR et non par chaîne d'agent**, F-73 : « la détection se fait
 * par capacité et par largeur, **JAMAIS par chaîne d'agent** — **la tablette pomme annonce
 * un ordinateur de bureau** ». `plancher:` est une variante dérivée de `--largeur-plancher`
 * dans `tokens.css`, et `scripts/verifier-le-plancher.mjs` fait échouer la construction si
 * le littéral de la traduction Tailwind et le token divergent.
 *
 * **Et « avant toute saisie » est tenu par la structure du document, pas par une
 * condition** : le formulaire n'est pas caché sous le refus, **les deux sont exclusifs par
 * la largeur**. Un refus affiché par-dessus un formulaire monté laisserait un champ de mot
 * de passe dans la page, atteignable au clavier.
 */

/** Les deux moitiés de cette page. La seconde n'existe qu'après un geste. */
type Mode = 'connexion' | 'code';

type Identifiants = { email: string; motDePasse: string };
type Reinitialisation = { email: string; code: string; motDePasse: string };

export function PageConnexion() {
  const [mode, poserLeMode] = useState<Mode>('connexion');
  const session = useSessionVivante();

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * **UNE SESSION VIVANTE EMMÈNE LA PERSONNE DANS SON ESPACE — CRITÈRE 3.**
   * ═══════════════════════════════════════════════════════════════════════
   *
   * F-71 : « elle **reconnaît une session vivante** et emmène la personne dans son espace
   * **sans redemander son mot de passe** ». Et le critère 3 en fait une exigence.
   *
   * **`useEffect` et non un rendu conditionnel** : partir vers une autre origine est un
   * effet de bord, pas un affichage. Le faire pendant le rendu est ce que React interdit,
   * et le symptôme serait une navigation déclenchée deux fois en mode strict.
   *
   * **UN `401` N'EST PAS UNE ERREUR ICI**, c'est le cas nominal : la plupart des visiteurs
   * de cette page n'ont pas de session. Rien ne s'affiche donc à ce sujet, et le formulaire
   * paraît — voir `useSessionVivante` pour le motif complet.
   */
  useEffect(() => {
    if (!session.isSuccess) return;
    window.location.assign(destinationApres(session.data.compte.role));
  }, [session.isSuccess, session.data]);

  return (
    <div className="flex min-h-dvh flex-col bg-fond-travail">
      {/* **L'écran de refus d'appareil**, cadrage §5.2 et F-71 critère 1. Il n'est pas
          caché : il occupe la page en dessous du plancher, et la vraie surface n'apparaît
          qu'au-dessus. Une interface cassée serait le seul autre choix.

          **`plancher:` est une variante de `tokens.css`**, pas un nombre écrit ici : elle
          vient de `--largeur-plancher`, « le paysage est obligatoire sur l'application de
          séance et sur la page de connexion qui y mène, à partir de 1024 de large ». Le
          jour où ce nombre change, il change à un seul endroit. */}
      <div className="flex flex-1 items-center justify-center p-8 plancher:hidden">
        <div className="flex max-w-formulaire flex-col gap-4 text-center">
          <Titre niveau={1}>Tournez votre appareil</Titre>
          <p className="text-16 leading-normal text-texte-faible">
            Swype se tient en paysage, sur un écran d’au moins 1024 points de
            large. C’est ce que demande le tableau : on y travaille au stylet,
            et une surface trop étroite ne le permet pas.
          </p>
          <p className="text-14 leading-normal text-texte-inactif">
            Le reste du site se lit sans difficulté depuis un téléphone.
          </p>
        </div>
      </div>

      <main className="hidden flex-1 items-center justify-center p-8 plancher:flex">
        <div className="flex w-full max-w-formulaire flex-col gap-6">
          <header className="flex flex-col gap-2">
            <span className="text-16 font-weight-800 tracking-marque text-texte-fort">
              SWYPE
            </span>
            <Titre niveau={1}>
              {mode === 'connexion' ? 'Se connecter' : 'Nouveau mot de passe'}
            </Titre>
          </header>

          {mode === 'connexion' ? (
            <FormulaireDeConnexion onMotDePasseOublie={() => poserLeMode('code')} />
          ) : (
            <FormulaireDeCode onRetour={() => poserLeMode('connexion')} />
          )}
        </div>
      </main>

      {/* « Elle porte le pied de page et pas le menu. » */}
      <PiedDePage />
    </div>
  );
}

/** La première moitié : adresse, mot de passe, et le chemin vers la seconde. */
function FormulaireDeConnexion({
  onMotDePasseOublie,
}: {
  readonly onMotDePasseOublie: () => void;
}) {
  const { register, handleSubmit } = useForm<Identifiants>();
  const ouverture = useConnexion();
  const echec =
    ouverture.error === null || ouverture.error === undefined
      ? null
      : lireLErreur(ouverture.error);

  return (
    <>
      <form
        className="flex flex-col gap-4"
        // `handleSubmit` rend une promesse, et `onSubmit` n'en attend pas : sans
        // le `void`, un rejet ne serait rattrapé nulle part. Le refus réel
        // est déjà porté par l'état de la mutation, que le bandeau lit.
        onSubmit={(evenement) =>
          void handleSubmit(async (valeurs) => {
            const ouverte = await ouverture.mutateAsync(valeurs);
            // **Une DESTINATION, jamais une valeur qui vaut accès.** La session
            // est déjà posée en cookie par le serveur ; cette page ne transporte
            // rien, elle change d'adresse.
            window.location.assign(destinationApres(ouverte.role));
          })(evenement)
        }
      >
        <Saisie
          libelle="Adresse email"
          type="email"
          autoComplete="username"
          {...register('email', { required: true })}
        />
        <Saisie
          libelle="Mot de passe"
          type="password"
          autoComplete="current-password"
          {...register('motDePasse', { required: true })}
        />

        {echec !== null && <Bandeau ton="echec">{echec.message}</Bandeau>}

        <Bouton type="submit" pleineLargeur enCours={ouverture.isPending}>
          Se connecter
        </Bouton>
      </form>

      {/* ═══════════════════════════════════════════════════════════════════
          **« MOT DE PASSE OUBLIÉ » EST UN GESTE SUR CETTE PAGE, ET NON UN LIEN.**
          ═══════════════════════════════════════════════════════════════════

          C'est le troisième et dernier élément de la liste fermée de F-71, **et il a été un
          lien mort jusqu'au 20260825** : il pointait vers `/mot-de-passe-oublie`, adresse
          qui ne correspond à aucune des huit pages, donc qui tombait sur la page de refus.

          **Deux règles s'y rencontraient sans que rien ne le signale.** La liste des huit
          est **fermée** (F-69), donc une page « mot de passe oublié » ne peut pas exister.
          Et « **le code de vérification n'est pas un lien** : c'est une valeur que la
          personne **saisit là où elle l'a demandée, sur cette page** » (F-71). La seconde
          règle donne la forme de la première : **il n'y avait pas de page à créer**, tout
          se passe ici. C'est le lien qui était l'erreur, pas l'absence de la page. */}
      <Bouton variante="fantome" onClick={onMotDePasseOublie}>
        Mot de passe oublié
      </Bouton>
    </>
  );
}

/**
 * **La seconde moitié : on demande un code, on le saisit ICI, on pose son mot de passe.**
 *
 * **Le mécanisme est unique, et c'est `CDC-1` qui le dit** : « il sert l'activation d'un
 * compte comme la réinitialisation ». Une personne dont le compte vient d'être créé et une
 * personne qui a oublié son mot de passe suivent donc **exactement** ce chemin.
 *
 * **LA RÉPONSE NE DIT PAS SI L'ADRESSE A UN COMPTE**, et l'écran ne le dit pas davantage :
 * la demande de code répond la même chose dans les deux cas, et le message affiché ici est
 * donc le même. **C'est la borne de `CDC-1` que F-71 nomme comme « le troisième point, le
 * plus fort et le moins évident »** — le produit « répond la même chose que l'adresse ait un
 * compte ou non ».
 *
 * **Les deux saisies vivent dans le même formulaire**, et c'est ce que « la personne saisit
 * la valeur là où elle l'a demandée » veut dire : elle n'a pas changé de page, son adresse
 * est déjà là, et le code arrive dans sa boîte.
 */
function FormulaireDeCode({ onRetour }: { readonly onRetour: () => void }) {
  const { register, handleSubmit, getValues } = useForm<Reinitialisation>();
  const demande = useDemandeDeCode();
  const pose = usePoseDeMotDePasse();

  const echec =
    pose.error === null || pose.error === undefined
      ? null
      : lireLErreur(pose.error);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(evenement) =>
        void handleSubmit(async (valeurs) => {
          const entree = await pose.mutateAsync(valeurs);
          window.location.assign(destinationApres(entree.role));
        })(evenement)
      }
    >
      <Saisie
        libelle="Adresse email"
        type="email"
        autoComplete="username"
        {...register('email', { required: true })}
      />

      {/* **Le code se demande sans quitter la page**, et le bouton n'est pas une soumission
          du formulaire : le formulaire, lui, pose le mot de passe. Deux gestes, un seul
          écran. */}
      <div className="flex flex-col gap-2">
        <Bouton
          variante="douce"
          enCours={demande.isPending}
          onClick={() => {
            demande.mutate(getValues('email'));
          }}
        >
          M’envoyer un code
        </Bouton>
        {demande.isSuccess && (
          // **Le message ne dit pas si le compte existe.** « La réponse est identique que
          // l'adresse ait un compte ou non », et un message qui dirait « code envoyé » sur
          // une adresse connue et autre chose sur une inconnue rétablirait l'écart que
          // `CDC-1` a fermé.
          <Bandeau ton="accent">
            Si cette adresse a un compte, un code vient d’y être envoyé. Il vaut
            dix minutes.
          </Bandeau>
        )}
      </div>

      <Saisie
        libelle="Code reçu"
        // **`autoComplete="one-time-code"`** : les navigateurs et les téléphones savent
        // proposer un code à usage unique lu dans un message. C'est la seule valeur qui
        // décrit ce champ, et une valeur fausse ferait proposer autre chose.
        autoComplete="one-time-code"
        {...register('code', { required: true })}
      />
      <Saisie
        libelle="Nouveau mot de passe"
        type="password"
        autoComplete="new-password"
        // **Douze caractères au minimum, aucune composition imposée**, `CDC-1` F-05. La
        // règle est tenue par le serveur, qui refuse et nomme son refus ; l'aide la dit
        // pour qu'on ne l'apprenne pas par un échec.
        aide="Douze caractères au minimum. Aucune majuscule ni chiffre imposés."
        {...register('motDePasse', { required: true })}
      />

      {echec !== null && <Bandeau ton="echec">{echec.message}</Bandeau>}

      <Bouton type="submit" pleineLargeur enCours={pose.isPending}>
        Poser ce mot de passe et entrer
      </Bouton>

      <Bouton variante="fantome" onClick={onRetour}>
        Revenir à la connexion
      </Bouton>
    </form>
  );
}
